import { useCallback, useState } from 'react'

export interface IPendingRows {
  /** `true` enquanto a gravação daquela linha está no ar. */
  isPending: (id: number) => boolean
  /** Marca a linha, roda a ação e desmarca no `finally` — mesmo se ela falhar. */
  run: (id: number, action: () => Promise<unknown>) => Promise<void>
}

/**
 * O estado de "esta linha está gravando", para o toggle que mora dentro de uma linha de
 * listagem — o switch de "ativo", "visível no site", "mostrar na home".
 *
 * Eram **16 sites em 10 arquivos, e só 3 desabilitavam o controle enquanto a requisição estava
 * no ar**: os outros 13 aceitavam clique repetido, e cada clique disparava um `PUT` que alterna
 * o valor no servidor. Dois cliques rápidos num "ativar" gravavam ativar e desativar, e a linha
 * terminava mostrando o resultado da resposta que chegasse por último. Os 3 que tratavam já
 * divergiam entre si: um `Set<number>` chamado `pendingShowOnFormsIds` e dois `number[]`
 * chamados `isUpdating`.
 *
 * É o mesmo raciocínio do `isLoading` que o `<ConfirmDialog>` passou a dar de graça a um
 * `onConfirm` assíncrono, e do autosave do `<MoneyBlurInput>`: a janela entre o clique e a
 * resposta é onde a gravação dupla acontece, e fechá-la não pode depender de cada tela lembrar.
 */
export const usePendingRows = (): IPendingRows => {
  const [pending, setPending] = useState<Set<number>>(new Set())

  const run = useCallback(async (id: number, action: () => Promise<unknown>) => {
    setPending((prev) => new Set(prev).add(id))

    try {
      await action()
    } finally {
      setPending((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }, [])

  const isPending = useCallback((id: number) => pending.has(id), [pending])

  return { isPending, run }
}
